import { hasOneMiteredEnd, isBoard } from "./board-helpers";
import { dustTotal } from "./Dust";
import {
  TutorialFacts,
  TutorialTrackId,
  TutorialTrackProgress,
} from "./GameState";
import { MachineId, MachineState } from "./Machine";
import { Board, MaterialInstance } from "./Materials";
import { formatMoney } from "../utils/formatNumber";
import { ownsTool } from "./progression-helpers";
import { hasSkill } from "./skill-helpers";
import { scenePlayed, StorySceneId } from "./story";
import { isNight } from "./time";
import {
  countOf,
  FIRST_MACHINE_IDS,
  hasShelf,
  hasShelfParts,
  isFullPalletBoard,
  isPallet,
  isPalletBoard,
  isRusticShelf,
  needsFirstPallet,
  palletOnBench,
  shelfLaidOut,
  shopMaterials,
  soldFirstPiece,
  TUTORIAL_MONEY_GOAL,
} from "./tutorial-goal";

export {
  needsFirstPallet,
  shopMaterials,
  TUTORIAL_MONEY_GOAL,
} from "./tutorial-goal";

/**
 * The tutorials: the character's own to-do lists, cards of goals with
 * checkboxes, derived from the shop rather than scripted. Each card is a
 * track — the guided opening that starts every new game, the first
 * night's lesson, and the sweeping lesson that goes up once the floor
 * first gets properly dusty — and each sub-step is a predicate over
 * GameState; a box is checked when its predicate holds (or the walk
 * already passed it), the first unchecked box of every up card is what
 * the coach points at, and `advanceTutorials` walks each track's flat
 * index forward inside the milestone pass.
 *
 * Two properties fall out of that, and both are the point:
 *
 * - It cannot desync. A player who wanders off, does a step early, or
 *   reloads mid-pallet still sees whatever is genuinely next, because the
 *   step is recomputed from durable state and never from what the UI
 *   thinks happened.
 * - It cannot lock. Nothing here gates input — the coach points, the
 *   player acts, and every predicate reads a condition the player can
 *   always reach again (scavenging is free and unlimited, the stand
 *   sells whatever gets set out on it, and dusty machines keep making
 *   dust).
 *
 * The story drips the list out (src/game/story.ts): a step names the
 * scene that reveals it (`revealedBy`), and until that scene has played
 * the box isn't on the card and the coach doesn't point at it. Through
 * the first build that is one thought per box — each box's thought
 * comes due the moment the box before it is ticked. Only the *visible*
 * length is story-driven — the ratchet walks hidden steps exactly as it
 * walks shown ones, so a player who gets ahead of the thoughts finds
 * the box already ticked when it appears.
 *
 * The prose lives in TutorialCard, not here, so instructions can name
 * their keys through the shortcut registry instead of hard-coding glyphs.
 * This file owns track begin conditions, goal titles, checkbox labels,
 * targets, reveals, and predicates.
 *
 * The opening ends deliberately with the first real machine set down on
 * the floor — from there the wants on the wall and the reputation gates
 * pace the shop's growth. Skip is one flag per track
 * (`tutorials[id].dismissed`), always offered, never punished.
 */

export const TUTORIAL_STEP_IDS = [
  "scavenge",
  "headHome",
  "unloadPallet",
  "dismantle",
  "layOutShelf",
  "buildShelf",
  "setUpStand",
  "sellShelf",
  "firstSale",
  "learnSkill",
  "goToStore",
  "grabCart",
  "addSawToCart",
  "checkOut",
  "gatherWood",
  "mountSaw",
  "cutParts",
  "assembleBirdhouse",
  "earnSavings",
  "buyMachine",
  "placeMachine",
  "driveHome",
  "getBroom",
  "sweepUp",
  "emptyPan",
] as const;
export type TutorialStepId = (typeof TUTORIAL_STEP_IDS)[number];

export const TUTORIAL_GOAL_IDS = [
  "firstItem",
  "sellIt",
  "birdhouse",
  "savings",
  "firstMachine",
  "callItADay",
  "sweepFloor",
] as const;
export type TutorialGoalId = (typeof TUTORIAL_GOAL_IDS)[number];

/**
 * Chrome a step can point at, marked with `data-tutorial-target`. Measured
 * from the DOM at highlight time the way reward flights measure their
 * targets — a target that isn't mounted (the phone is closed, the store
 * aisle isn't open) simply doesn't light up, which is not an error.
 */
export const TUTORIAL_DOM_TARGET_IDS = [
  "navbar-journal",
  "store-corral",
  "store-tool-handSaw",
  "store-tool-broom",
  "store-checkout",
  "store-machine-miterSaw",
  "skill-rusticProjects",
  /** The hand saw's tile in the bench view's arms tray. */
  "bench-arms-handSaw",
  /** The first bare hook on the bench view's tool rail. */
  "bench-hook",
] as const;
export type TutorialDomTargetId = (typeof TUTORIAL_DOM_TARGET_IDS)[number];

export const TUTORIAL_TARGET_ATTRIBUTE = "data-tutorial-target";

/** Something in the world or on the screen the current step points at. */
export type TutorialTarget =
  | { readonly kind: "machine"; readonly machineTypeId: MachineId }
  | { readonly kind: "truck"; readonly part: "cab" | "bed" }
  | { readonly kind: "stand" }
  | { readonly kind: "broom" }
  | {
      readonly kind: "pile";
      readonly match: (material: MaterialInstance) => boolean;
    }
  | { readonly kind: "dom"; readonly id: TutorialDomTargetId };

export interface TutorialStep {
  readonly id: TutorialStepId;
  /** The checkbox line on the to-do card, short enough to stay one line. */
  readonly label: string;
  /** What lights up while this step is the first unchecked box. */
  readonly targets: ReadonlyArray<TutorialTarget>;
  /** True once the player has done the thing. Never un-satisfies. */
  readonly satisfied: (gameState: TutorialFacts) => boolean;
  /**
   * The story scene whose thought puts this box on the card. Absent,
   * the box is there from the start. See the header.
   */
  readonly revealedBy?: StorySceneId;
}

/** One entry on the to-do list: a goal and the steps that achieve it. */
export interface TutorialGoal {
  readonly id: TutorialGoalId;
  /** The heading the checkboxes sit under. */
  readonly title: string;
  readonly steps: ReadonlyArray<TutorialStep>;
}

// ---------------------------------------------------------------------------
// Reading the shop
// ---------------------------------------------------------------------------

// The first build's questions — is the pallet home, are the boards
// freed, is the plan laid out — are tutorial-goal.ts's, because the
// story's thoughts ask them too.

const isBirdhouse = (material: MaterialInstance) =>
  material.type === "birdhouse";

/** The birdhouse's cut list, every piece a crosscut of a pallet board:
 * two 12" fronts with one end mitered 45°, a 12" roof, a 12" floor, and
 * two 6" side walls. Stated here for the same reason as
 * SHELF_BOARD_COUNT (tutorial-goal.ts); two full pallet boards cover
 * it. */
const BIRDHOUSE_PART_LENGTH_IN = 12;
const BIRDHOUSE_SIDE_LENGTH_IN = 6;
const BIRDHOUSE_MITERED_COUNT = 2;
const BIRDHOUSE_TWELVES_COUNT = 4;
const BIRDHOUSE_SIDES_COUNT = 2;
const BIRDHOUSE_BOARDS_NEEDED = 2;

const hasBirdhouse = (gameState: TutorialFacts) =>
  countOf(gameState, isBirdhouse) > 0;

/** The trip is over with the pallet aboard: home, or anywhere later. */
const backWithPallet = (gameState: TutorialFacts): boolean =>
  !needsFirstPallet(gameState) && gameState.player.away?.kind !== "scavenging";

const isBirdhousePartStock = (material: MaterialInstance): material is Board =>
  isPalletBoard(material) && isBoard(material);

const hasBirdhouseParts = (gameState: TutorialFacts): boolean => {
  const parts = shopMaterials(gameState).filter(isBirdhousePartStock);
  const twelves = parts.filter(
    (board) => board.length === BIRDHOUSE_PART_LENGTH_IN,
  );
  const mitered = twelves.filter((board) => hasOneMiteredEnd(board, 45));
  const sides = parts.filter(
    (board) => board.length === BIRDHOUSE_SIDE_LENGTH_IN,
  );
  return (
    mitered.length >= BIRDHOUSE_MITERED_COUNT &&
    twelves.length >= BIRDHOUSE_TWELVES_COUNT &&
    sides.length >= BIRDHOUSE_SIDES_COUNT
  );
};

/** Enough wood on hand for the birdhouse's cut list, in any state of
 * progress: a whole pallet, enough uncut boards, the parts themselves, or
 * the finished piece. */
const hasBirdhouseWood = (gameState: TutorialFacts): boolean =>
  countOf(gameState, isPallet) > 0 ||
  countOf(gameState, isFullPalletBoard) >= BIRDHOUSE_BOARDS_NEEDED ||
  hasBirdhouseParts(gameState) ||
  hasBirdhouse(gameState);

const isHandSawItem = (material: MaterialInstance) =>
  material.type === "tool" && material.toolId === "handSaw";

const shoppingCart = (gameState: TutorialFacts) =>
  gameState.player.away?.kind === "shopping"
    ? gameState.player.away.cart
    : null;

const sawInCart = (gameState: TutorialFacts): boolean =>
  (shoppingCart(gameState) ?? []).some(
    (line) => line.kind === "material" && isHandSawItem(line.material),
  );

const hasSaw = (gameState: TutorialFacts) => ownsTool(gameState, "handSaw");

const sawMounted = (gameState: TutorialFacts) =>
  gameState.machines.some((machine) => machine.tools.includes("handSaw"));

const reachedMoneyGoal = (gameState: TutorialFacts) =>
  gameState.money >= TUTORIAL_MONEY_GOAL;

const isFirstMachine = (machine: MachineState): boolean =>
  FIRST_MACHINE_IDS.includes(machine.machineTypeId);

/** A real machine standing on the shop floor. */
const placedFirstMachine = (gameState: TutorialFacts): boolean =>
  gameState.machines.some(isFirstMachine);

/** A real machine bought, wherever it is: in the cart, in the bed,
 * crated on the floor, on the shoulders, or standing. */
const boughtFirstMachine = (gameState: TutorialFacts): boolean =>
  placedFirstMachine(gameState) ||
  gameState.machineCrates.some((crate) => isFirstMachine(crate.machine)) ||
  gameState.truck.crates.some(isFirstMachine) ||
  (gameState.player.carriedMachine != null &&
    isFirstMachine(gameState.player.carriedMachine)) ||
  (shoppingCart(gameState) ?? []).some(
    (line) =>
      line.kind === "machine" && FIRST_MACHINE_IDS.includes(line.machineTypeId),
  );

/** The first night is over once a morning has come. */
const sleptOnce = (gameState: TutorialFacts): boolean =>
  gameState.day > 1 || gameState.player.away?.kind === "home";

// ---------------------------------------------------------------------------
// The goals
// ---------------------------------------------------------------------------

/**
 * Each predicate is written cumulatively — "this step's product exists, or
 * something only a later step could have produced does". A player who gets
 * ahead of the coach (dismantles a second pallet before the card catches
 * up, sets out work before it says to) skips forward instead of stranding
 * it on a condition that has already come and gone. The placed machine is
 * the terminal fallback: a shop with a real saw on the floor has outgrown
 * the list.
 */
const OPENING_GOALS: ReadonlyArray<TutorialGoal> = [
  {
    // Her listing, his verdict: the heading is the thought that started
    // it. One box per thought from here to the finished shelf.
    id: "firstItem",
    title: "I could make that...",
    steps: [
      {
        id: "scavenge",
        label: "Scavenge a pallet",
        targets: [{ kind: "truck", part: "cab" }],
        satisfied: (gameState) => !needsFirstPallet(gameState),
        revealedBy: "opener",
      },
      {
        // Ticks the tick the truck pulls back in; the card is behind
        // the trip's screen while the box is open, so the thought is
        // the instruction and the box is the record of it.
        id: "headHome",
        label: "Drive back to the shop",
        targets: [],
        satisfied: backWithPallet,
        revealedBy: "palletFound",
      },
      {
        id: "unloadPallet",
        label: "Carry the pallet to the workbench",
        targets: [
          { kind: "truck", part: "bed" },
          { kind: "pile", match: isPallet },
          { kind: "machine", machineTypeId: "workspace" },
        ],
        satisfied: (gameState) =>
          palletOnBench(gameState) ||
          hasShelfParts(gameState) ||
          hasShelf(gameState) ||
          soldFirstPiece(gameState),
        revealedBy: "palletHome",
      },
      {
        id: "dismantle",
        label: "Pry the nails out",
        targets: [{ kind: "machine", machineTypeId: "workspace" }],
        satisfied: (gameState) =>
          hasShelfParts(gameState) ||
          hasShelf(gameState) ||
          soldFirstPiece(gameState),
        revealedBy: "palletOnBench",
      },
      {
        id: "layOutShelf",
        label: "Lay the boards out on the shelf plan",
        targets: [{ kind: "machine", machineTypeId: "workspace" }],
        satisfied: (gameState) =>
          shelfLaidOut(gameState) ||
          hasShelf(gameState) ||
          soldFirstPiece(gameState),
        revealedBy: "boardsFreed",
      },
      {
        id: "buildShelf",
        label: "Nail it together",
        targets: [{ kind: "machine", machineTypeId: "workspace" }],
        satisfied: (gameState) =>
          hasShelf(gameState) || soldFirstPiece(gameState),
        revealedBy: "partsPlaced",
      },
    ],
  },
  {
    id: "sellIt",
    title: "Sell the shelf",
    steps: [
      {
        id: "setUpStand",
        label: "Set up a table at the road",
        targets: [{ kind: "stand" }],
        satisfied: (gameState) =>
          gameState.standSetUp || soldFirstPiece(gameState),
        revealedBy: "shelfBuilt",
      },
      {
        // Satisfied by exactly what the label says: the shelf is out on
        // the stand. Transient (it can be taken back), but the walk
        // ratchets past it the tick it first holds; the sale keeps it
        // satisfied in a shop reloaded after the shelf sold.
        id: "sellShelf",
        label: "Set the shelf out for sale",
        targets: [{ kind: "stand" }],
        satisfied: (gameState) =>
          gameState.stand.some(isRusticShelf) || soldFirstPiece(gameState),
        revealedBy: "shelfBuilt",
      },
      {
        // The sale gate, worn honestly as its own box: the next goal
        // starts at the store, and the store is what the first sale
        // unlocks — so the list holds here until the money exists to
        // spend. The wait is short: while the first sale is pending the
        // clock runs at working pace (the StreetSystem's first-sale
        // spender), so the dealt buyer's stroll to the stand passes in
        // seconds.
        id: "firstSale",
        label: "Wait for a buyer",
        targets: [{ kind: "stand" }],
        satisfied: soldFirstPiece,
        revealedBy: "shelfBuilt",
      },
    ],
  },
  {
    id: "birdhouse",
    title: "Build a birdhouse",
    steps: [
      {
        id: "learnSkill",
        label: "Research Rustic Projects",
        targets: [
          { kind: "dom", id: "navbar-journal" },
          { kind: "dom", id: "skill-rusticProjects" },
        ],
        satisfied: (gameState) =>
          hasSkill(gameState.progression, "rusticProjects") ||
          reachedMoneyGoal(gameState),
        revealedBy: "firstSale",
      },
      {
        id: "goToStore",
        label: "Drive to the Orange Box",
        targets: [{ kind: "truck", part: "cab" }],
        satisfied: (gameState) =>
          (gameState.player.away?.kind === "shopping" &&
            gameState.player.away.store === "orangeBox") ||
          sawInCart(gameState) ||
          hasSaw(gameState) ||
          reachedMoneyGoal(gameState),
        revealedBy: "skillLearned",
      },
      {
        // The shelves only load a cart you're pushing, so the corral by
        // the entrance is the first stop of every shopping run.
        id: "grabCart",
        label: "Grab a cart by the entrance",
        targets: [{ kind: "dom", id: "store-corral" }],
        satisfied: (gameState) =>
          (gameState.player.away?.kind === "shopping" &&
            gameState.player.away.hasCart) ||
          sawInCart(gameState) ||
          hasSaw(gameState) ||
          reachedMoneyGoal(gameState),
        revealedBy: "skillLearned",
      },
      {
        id: "addSawToCart",
        label: "Put a hand saw in the cart",
        targets: [{ kind: "dom", id: "store-tool-handSaw" }],
        satisfied: (gameState) =>
          sawInCart(gameState) ||
          hasSaw(gameState) ||
          reachedMoneyGoal(gameState),
        revealedBy: "skillLearned",
      },
      {
        id: "checkOut",
        label: "Check out and head home",
        targets: [{ kind: "dom", id: "store-checkout" }],
        satisfied: (gameState) =>
          (hasSaw(gameState) && gameState.player.away?.kind !== "shopping") ||
          reachedMoneyGoal(gameState),
        revealedBy: "skillLearned",
      },
      {
        id: "gatherWood",
        label: "Scavenge another pallet if needed",
        targets: [{ kind: "truck", part: "cab" }],
        satisfied: (gameState) =>
          hasBirdhouseWood(gameState) || reachedMoneyGoal(gameState),
        revealedBy: "sawHome",
      },
      {
        // The saw comes home in the arms. Out on the floor this lights
        // the saw (if set down) and the bench; leaned over the bench it
        // lights the saw's tile in the arms tray and the bare hook it
        // goes on — the chrome targets only exist there, and light only
        // there.
        id: "mountSaw",
        label: "Hang the saw on the workbench's rail",
        targets: [
          { kind: "pile", match: isHandSawItem },
          { kind: "machine", machineTypeId: "workspace" },
          { kind: "dom", id: "bench-arms-handSaw" },
          { kind: "dom", id: "bench-hook" },
        ],
        satisfied: (gameState) =>
          sawMounted(gameState) ||
          hasBirdhouseParts(gameState) ||
          hasBirdhouse(gameState) ||
          reachedMoneyGoal(gameState),
        revealedBy: "sawHome",
      },
      {
        id: "cutParts",
        label: "Cut the parts to length",
        targets: [{ kind: "machine", machineTypeId: "workspace" }],
        satisfied: (gameState) =>
          hasBirdhouseParts(gameState) ||
          hasBirdhouse(gameState) ||
          reachedMoneyGoal(gameState),
        revealedBy: "sawMounted",
      },
      {
        id: "assembleBirdhouse",
        label: "Nail the birdhouse together",
        targets: [{ kind: "machine", machineTypeId: "workspace" }],
        satisfied: (gameState) =>
          hasBirdhouse(gameState) || reachedMoneyGoal(gameState),
        revealedBy: "sawMounted",
      },
    ],
  },
  {
    id: "savings",
    title: "Make some money",
    steps: [
      {
        id: "earnSavings",
        label: `Sell my work — save up ${formatMoney(TUTORIAL_MONEY_GOAL)}`,
        targets: [{ kind: "stand" }],
        satisfied: (gameState) =>
          reachedMoneyGoal(gameState) || boughtFirstMachine(gameState),
        revealedBy: "birdhouseBuilt",
      },
    ],
  },
  {
    id: "firstMachine",
    title: "Get a real saw",
    steps: [
      {
        id: "buyMachine",
        label: "Buy a miter saw at the Orange Box",
        targets: [
          { kind: "truck", part: "cab" },
          { kind: "dom", id: "store-machine-miterSaw" },
          { kind: "dom", id: "store-checkout" },
        ],
        satisfied: boughtFirstMachine,
        revealedBy: "savingsReached",
      },
      {
        id: "placeMachine",
        label: "Carry it in and set it down",
        targets: [{ kind: "truck", part: "bed" }],
        satisfied: placedFirstMachine,
        revealedBy: "savingsReached",
      },
    ],
  },
];

/**
 * The first night's lesson: the day only ends by driving home. One box,
 * ticked by the morning after (or by the drive itself).
 */
const FIRST_NIGHT_GOALS: ReadonlyArray<TutorialGoal> = [
  {
    id: "callItADay",
    title: "Call it a day",
    steps: [
      {
        id: "driveHome",
        label: "Drive home and sleep",
        targets: [{ kind: "truck", part: "cab" }],
        satisfied: sleptOnce,
        revealedBy: "firstNight",
      },
    ],
  },
];

/**
 * The sweeping lesson. Its steps are the broom loop end to end: the pan
 * holding dust proves a sweep happened, and the pan coming back up empty
 * proves it was poured out at the can — the only drain it has. Transient
 * conditions are fine here because the walk ratchets: the frontier moves
 * the tick a predicate first holds and never comes back.
 */
const DUST_GOALS: ReadonlyArray<TutorialGoal> = [
  {
    id: "sweepFloor",
    title: "Sweep the floor",
    steps: [
      {
        id: "getBroom",
        label: "Buy a broom at the Orange Box",
        targets: [
          { kind: "truck", part: "cab" },
          { kind: "dom", id: "store-tool-broom" },
        ],
        satisfied: (gameState) => gameState.broomOwned,
        revealedBy: "floorDusty",
      },
      {
        id: "sweepUp",
        label: "Sweep dust into the pan",
        targets: [{ kind: "broom" }],
        satisfied: (gameState) => dustTotal(gameState.dustpan) > 0,
        revealedBy: "floorDusty",
      },
      {
        id: "emptyPan",
        label: "Empty the pan at the garbage can",
        targets: [{ kind: "machine", machineTypeId: "garbageCan" }],
        satisfied: (gameState) => dustTotal(gameState.dustpan) === 0,
        revealedBy: "floorDusty",
      },
    ],
  },
];

/** One to-do card: when it goes up, and the goals written on it. */
export interface TutorialTrack {
  readonly id: TutorialTrackId;
  /** Whether the card has gone up yet. Reads a one-way condition, so a
   * begun track never un-begins. */
  readonly begun: (gameState: TutorialFacts) => boolean;
  readonly goals: ReadonlyArray<TutorialGoal>;
}

/** Every track, in the order their cards stack on the wall. */
export const TUTORIAL_TRACKS: ReadonlyArray<TutorialTrack> = [
  { id: "opening", begun: () => true, goals: OPENING_GOALS },
  {
    id: "firstNight",
    begun: (gameState) => sleptOnce(gameState) || isNight(gameState),
    goals: FIRST_NIGHT_GOALS,
  },
  {
    id: "dust",
    begun: (gameState) => gameState.progression.sweepingUnlocked,
    goals: DUST_GOALS,
  },
];

/** Each track's goals flattened to the walk order its stored index counts in. */
const STEPS_BY_TRACK: Readonly<
  Record<TutorialTrackId, ReadonlyArray<TutorialStep>>
> = {
  opening: OPENING_GOALS.flatMap((goal) => goal.steps),
  firstNight: FIRST_NIGHT_GOALS.flatMap((goal) => goal.steps),
  dust: DUST_GOALS.flatMap((goal) => goal.steps),
};

/** A track's steps in walk order — what its stored index counts in. */
export function trackSteps(
  trackId: TutorialTrackId,
): ReadonlyArray<TutorialStep> {
  return STEPS_BY_TRACK[trackId];
}

/** Whether a step's thought has been thought — its box is on the card. */
export function stepRevealed(
  gameState: TutorialFacts,
  step: TutorialStep,
): boolean {
  return (
    step.revealedBy === undefined ||
    scenePlayed(gameState.progression.story, step.revealedBy)
  );
}

/**
 * Walk every track's step index past everything the shop already
 * satisfies. Called from the milestone pass, which runs every tick, so
 * the cards keep up with whatever the player did without any action
 * needing to know the tutorials exist. Returns the incoming record
 * untouched when nothing moved, so the pass can compare by identity.
 *
 * Tracks walk whether or not their card is up yet, and whether or not a
 * step is revealed yet: a player who buys a broom and sweeps before the
 * floor ever gets bad has already taken the lesson, and the card skips
 * what they've shown they know.
 */
export function advanceTutorials(
  gameState: TutorialFacts,
): Readonly<Record<TutorialTrackId, TutorialTrackProgress>> {
  let tutorials = gameState.progression.tutorials;
  for (const track of TUTORIAL_TRACKS) {
    const progress = tutorials[track.id];
    const steps = STEPS_BY_TRACK[track.id];
    let step = progress.step;
    while (step < steps.length && steps[step].satisfied(gameState)) {
      step++;
    }
    if (step !== progress.step) {
      tutorials = { ...tutorials, [track.id]: { ...progress, step } };
    }
  }
  return tutorials;
}

/** The step a track's coach is on, or null while its card isn't up —
 * not begun yet, skipped, every box ticked — or while the step's
 * thought hasn't arrived yet (the coach points at nothing the card
 * doesn't show). */
export function currentTutorialStep(
  gameState: TutorialFacts,
  trackId: TutorialTrackId,
): TutorialStep | null {
  const track = TUTORIAL_TRACKS.find((t) => t.id === trackId);
  const progress = gameState.progression.tutorials[trackId];
  if (track === undefined || progress.dismissed || !track.begun(gameState)) {
    return null;
  }
  const step = STEPS_BY_TRACK[trackId][progress.step] ?? null;
  return step !== null && stepRevealed(gameState, step) ? step : null;
}

/** A to-do card's view of its current goal: its steps and which boxes
 * are checked right now. */
export interface TutorialGoalView {
  readonly trackId: TutorialTrackId;
  readonly goal: TutorialGoal;
  /** The goal's steps whose thoughts have arrived — what the card draws. */
  readonly steps: ReadonlyArray<TutorialStep>;
  /** Aligned with `steps`: passed by the walk, or satisfied live. */
  readonly checked: ReadonlyArray<boolean>;
}

/**
 * The goal holding a track's current step, with each box's checked state.
 * A box is checked once the walk has passed it — the walk only moves
 * forward, so a condition that has come and gone (the pallet that got
 * pried, the pan that got poured out) stays checked — or while its
 * predicate holds, so work done ahead of the coach shows up immediately.
 *
 * Only revealed steps are on the view; a goal none of whose steps have
 * been revealed yet is no card at all. The goal is found from the walk's
 * frontier whether or not that step is revealed, so a card whose next
 * thought is still on its way keeps showing the boxes it has.
 */
export function currentTutorialGoalView(
  gameState: TutorialFacts,
  trackId: TutorialTrackId,
): TutorialGoalView | null {
  const track = TUTORIAL_TRACKS.find((t) => t.id === trackId);
  const progress = gameState.progression.tutorials[trackId];
  if (track === undefined || progress.dismissed || !track.begun(gameState)) {
    return null;
  }
  const steps = STEPS_BY_TRACK[trackId];
  const frontier = steps[progress.step] ?? null;
  if (frontier === null) return null;
  const goal = track.goals.find((g) => g.steps.includes(frontier));
  if (goal === undefined) return null;
  const shown = goal.steps.filter((s) => stepRevealed(gameState, s));
  if (shown.length === 0) return null;
  const walkFrontier = progress.step;
  return {
    trackId,
    goal,
    steps: shown,
    checked: shown.map(
      (s) => steps.indexOf(s) < walkFrontier || s.satisfied(gameState),
    ),
  };
}

/** Every card on the wall right now, in track order — the opening on
 * top, lessons that begin later beneath it. */
export function tutorialViews(
  gameState: TutorialFacts,
): ReadonlyArray<TutorialGoalView> {
  return TUTORIAL_TRACKS.flatMap((track) => {
    const view = currentTutorialGoalView(gameState, track.id);
    return view === null ? [] : [view];
  });
}

/** The current step of every up card — everything the coach is pointing
 * at, across all tracks. */
export function activeTutorialSteps(
  gameState: TutorialFacts,
): ReadonlyArray<TutorialStep> {
  return TUTORIAL_TRACKS.flatMap((track) => {
    const step = currentTutorialStep(gameState, track.id);
    return step === null ? [] : [step];
  });
}
